import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, CheckCircle, Settings, Mail, Phone, Filter, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';
import CleaningOptionsComparison from '../components/CleaningOptionsComparison';
import AlternativesBand from '../components/AlternativesBand';
import UnifiedContactModal from '../components/UnifiedContactModal';

const GNGSeriesPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="GNG In-Line Filters | Budget In-Line Filtration, Ring-Lock Head"
        description="Economical in-line filtration with quick-release ring-lock head, painted mild-steel body, and multiple cleaning options."
        canonicalPath="/filtration/inline/gng"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">In-Line Pressure Filters</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">GNG (Economy)</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                GNG Series In-Line Filters (Economy)
              </h1>
              <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                Budget-friendly in-line filtration with ring-lock quick-release head—painted mild-steel body in CTC blue.
              </p>

              <div className="bg-blue-50 border-l-4 border-green-600 p-6 rounded-r-lg mb-8">
                <p className="text-gray-800 leading-relaxed">
                  GNG filters deliver reliable inline filtration at an economical price point. The ring-lock quick-release head design enables fast screen removal and cleaning without tools, while the painted mild-steel construction keeps upfront costs low.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg"
                >
                  Get a Quote
                </button>
                <Link
                  to="/contact?topic=filtration"
                  className="border-2 border-green-600 text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-colors text-center"
                >
                  Talk to a Filtration Specialist
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img src="/GNG.jpeg" alt="GNG Series Economy In-Line Filter" className="w-full h-auto" />
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-2">GNG Series Filter</h3>
                  <p className="text-sm text-gray-700">Budget-friendly inline filtration with ring-lock quick-release head.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cleaning Options Info Strip */}
      <section className="py-6 bg-gradient-to-r from-green-50 to-blue-50 border-y border-green-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Three Cleaning Approaches Available</h3>
              <p className="text-gray-700 text-sm">
                Supports Manual, Semi-Automatic (BIF & MVC), and Fully Automatic (RAB & AVC) cleaning options.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/filtration/inline/cleaning/manual"
                className="text-xs bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Manual
              </Link>
              <Link
                to="/filtration/inline/cleaning/semi-auto"
                className="text-xs bg-white border border-ctc-blue text-ctc-blue px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Semi-Auto
              </Link>
              <Link
                to="/filtration/inline/cleaning/automatic"
                className="text-xs bg-white border border-green-600 text-green-600 px-4 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors"
              >
                Fully Auto
              </Link>
              <Link
                to="/filtration/inline/cleaning"
                className="text-xs bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Compare All
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose GNG?</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Low Upfront Cost</h3>
              <p className="text-gray-700 leading-relaxed">
                Simplified body and ring-lock head design reduce manufacturing complexity and cost.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="text-ctc-blue" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quick Maintenance</h3>
              <p className="text-gray-700 leading-relaxed">
                Quick-release clamp on ring-lock head for fast screen removal and cleaning.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Filter className="text-orange-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Painted Mild Steel</h3>
              <p className="text-gray-700 leading-relaxed">
                Blue epoxy finish matching the BIG line for durable corrosion resistance.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Application Note */}
      <section className="py-8 bg-yellow-50 border-y border-yellow-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-start">
            <AlertTriangle className="text-yellow-600 mr-4 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Engineering Note</h3>
              <p className="text-gray-700 leading-relaxed">
                Ring-lock head requires pressure to complete the seal; <strong>do not use on suction-side service</strong>. GNG filters are designed exclusively for pressurized applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Models & Dimensions Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">GNG Series (Non-Gasketed) — Specifications</h2>
          <p className="text-center text-gray-600 mb-8">
            Catalog-exact GNG specifications
          </p>

          <div className="mb-8 flex justify-center">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{maxWidth: '600px'}}>
              <img src="/GNG Diagram.png" alt="GNG Series Technical Diagram" className="w-full h-auto" />
              <div className="p-4 bg-gray-50">
                <p className="text-sm text-gray-700 text-center"><span className="font-semibold">Technical Diagram:</span> GNG ring-lock head design and dimensions</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Item #</th>
                  <th className="px-4 py-3 text-left font-semibold">Flow (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">Inlet Size</th>
                  <th className="px-4 py-3 text-left font-semibold">Outlet Size</th>
                  <th className="px-4 py-3 text-left font-semibold">A (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">B – Screen Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">C (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">D (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">E (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">F (in)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA418</td>
                  <td className="px-4 py-3">500</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">2½</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">40½</td>
                  <td className="px-4 py-3">7¼</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA417</td>
                  <td className="px-4 py-3">800</td>
                  <td className="px-4 py-3">6</td>
                  <td className="px-4 py-3">6</td>
                  <td className="px-4 py-3">35½</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">52¾</td>
                  <td className="px-4 py-3">8¼</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA419</td>
                  <td className="px-4 py-3">1000</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">12</td>
                  <td className="px-4 py-3">64¾</td>
                  <td className="px-4 py-3">9¾</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA416</td>
                  <td className="px-4 py-3">1200</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">76</td>
                  <td className="px-4 py-3">10¾</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA403</td>
                  <td className="px-4 py-3">1500</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">90</td>
                  <td className="px-4 py-3">90</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">106</td>
                  <td className="px-4 py-3">10¾</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA414</td>
                  <td className="px-4 py-3">1500</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">90</td>
                  <td className="px-4 py-3">90</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">106</td>
                  <td className="px-4 py-3">10¾</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">BA579</td>
                  <td className="px-4 py-3">2000</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">135</td>
                  <td className="px-4 py-3">10¾</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-600 mt-4 text-center italic">
            B = Screen Length. All units ship with universal ports. Keep units and fractions exactly as shown.
          </p>
        </div>
      </section>

      {/* Screen Options */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Screen Options</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-green-600 transition-all"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Perforated Round-Hole</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>High open area for maximum flow</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>Ideal for coarse filtration and high solids</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>Available in standard increments</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-200 hover:border-green-600 transition-all"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Woven Mesh</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>Fine filtration for drip and micro-irrigation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>Standard mesh sizes (20–100)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <span>Precision control of particle size</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cleaning Options */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <CleaningOptionsComparison series="GNG" />
        </div>
      </section>

      {/* Downloads */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Downloads</h2>

          <div className="bg-gray-50 rounded-lg shadow-lg p-8">
            <div className="space-y-4">
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-white transition-colors"
              >
                <div className="flex items-center">
                  <Download className="text-green-600 mr-4" size={24} />
                  <span className="font-semibold text-gray-900">GNG Data Sheet (PDF)</span>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </a>

              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-white transition-colors"
              >
                <div className="flex items-center">
                  <Download className="text-green-600 mr-4" size={24} />
                  <span className="font-semibold text-gray-900">Installation & Maintenance Manual (PDF)</span>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </a>

              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-white transition-colors"
              >
                <div className="flex items-center">
                  <Download className="text-green-600 mr-4" size={24} />
                  <span className="font-semibold text-gray-900">Dimensional Drawing Pack (ZIP)</span>
                </div>
                <ArrowRight className="text-gray-400" size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Alternatives Band */}
      <AlternativesBand
        alternatives={[
          {
            title: "Want precision gasket-sealed filtration?",
            description: "BIG Series offers precision, gasket-sealed in-line filters with optional automation upgrades and leak-free performance.",
            link: "/filtration/inline/big"
          },
          {
            title: "Prefer stainless at a value price?",
            description: "BIG-S provides precision filtration in a cost-optimized stainless body with modular ports for flexible installations.",
            link: "/filtration/inline/big-s"
          }
        ]}
      />

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Budget-Friendly Filtration Without Compromise</h2>
          <p className="text-xl text-green-100 mb-8">
            Get reliable inline filtration at an economical price. Talk to a CTC specialist today to configure your GNG system.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Get a Quote
            </button>

            <Link
              to="/contact?topic=filtration"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Talk to a Specialist
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-green-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="product_quote"
      />
    </div>
  );
};

export default GNGSeriesPage;
